import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { FEATURES } from "../config/features";
import { STORYDOODLE_PLUS_MESSAGING } from "../config/messaging";
import { useUserAccount } from "../hooks/useUserAccount";
import { apiClient } from "../lib/api";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Spinner } from "./ui/spinner";

interface QuotaUpgradePromptProps {
    /**
     * Whether to show as a modal or inline
     */
    variant?: "modal" | "inline";

    /**
     * Urgency level determines the message and styling
     */
    urgency?: "approaching" | "exceeded" | "promote";

    /**
     * Callback when user dismisses the prompt
     */
    onDismiss?: () => void;
}

export function QuotaUpgradePrompt({ variant = "inline", urgency = "exceeded", onDismiss }: QuotaUpgradePromptProps) {
    const { tier, paymentPlan, isLoading } = useUserAccount();
    const [isUpgrading, setIsUpgrading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const posthog = usePostHog();

    // Track when upgrade prompt is shown (before early returns)
    // Show for free users OR trial users (who have plus tier but trial payment plan)
    const shouldShow = FEATURES.PREMIUM_ENABLED && (tier === "free" || paymentPlan === "trial");
    useEffect(() => {
        if (shouldShow) {
            posthog.capture("upgrade_prompt_shown", {
                variant,
                urgency,
                timestamp: new Date().toISOString(),
            });
        }
    }, [posthog, variant, urgency, shouldShow]);

    // Don't show if conditions aren't met
    if (!shouldShow) {
        return null;
    }

    const handleUpgrade = async (billingCycle: "monthly" | "annual") => {
        setIsUpgrading(true);
        setError(null);

        // Track upgrade attempt
        posthog.capture("upgrade_prompt_clicked", {
            variant,
            urgency,
            billing_cycle: billingCycle,
            timestamp: new Date().toISOString(),
        });

        try {
            const { checkout_url } = await apiClient.createTierCheckout("plus", billingCycle);
            window.location.href = checkout_url;
        } catch (err) {
            console.error("Failed to create checkout:", err);
            setError("Failed to start upgrade process. Please try again.");
            setIsUpgrading(false);
        }
    };

    const handleDismiss = () => {
        // Track when user dismisses the upgrade prompt
        posthog.capture("upgrade_prompt_dismissed", {
            variant,
            urgency,
            timestamp: new Date().toISOString(),
        });

        onDismiss?.();
    };

    // Dynamic messaging based on urgency level and user type
    const getUpgradeMessage = () => {
        // Trial-specific messaging with urgency
        if (paymentPlan === "trial") {
            switch (urgency) {
                case "approaching":
                    return "Getting close to daily limits! Don't lose access - subscribe now to keep your StoryDoodle+ features when your trial ends!";
                case "exceeded":
                    return "You've reached some daily limits. Subscribe now to ensure uninterrupted access to StoryDoodle+ features!";
                case "promote":
                    return "Your trial gives you a taste of StoryDoodle+ - subscribe now to keep these amazing features forever!";
                default:
                    return "Don't lose your StoryDoodle+ access! Subscribe now to continue enjoying premium features after your trial ends.";
            }
        }

        // Standard messaging for free users - use centralized messaging
        return STORYDOODLE_PLUS_MESSAGING.upgradePrompts[urgency] || STORYDOODLE_PLUS_MESSAGING.upgradePrompts.default;
    };

    // For promotional variant, show a subtle inline upgrade option with full billing options
    if (urgency === "promote") {
        return (
            <div className="mt-4 p-4 border border-primary/20 rounded-lg bg-primary/5">
                <div className="space-y-3">
                    <div className="text-sm font-medium text-secondary">{getUpgradeMessage()}</div>

                    {error && (
                        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-2">
                            <p className="text-xs text-destructive">{error}</p>
                        </div>
                    )}

                    <div className="flex gap-4">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpgrade("monthly")}
                            disabled={isUpgrading || isLoading}
                            className="text-xs flex-1"
                        >
                            {isUpgrading ? (
                                <Spinner className="h-3 w-3" />
                            ) : (
                                `Monthly - ${STORYDOODLE_PLUS_MESSAGING.pricing.monthly}`
                            )}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpgrade("annual")}
                            disabled={isUpgrading || isLoading}
                            className="text-xs flex-1"
                        >
                            Annual - ${STORYDOODLE_PLUS_MESSAGING.pricing.annual}
                        </Button>
                    </div>

                    <div className="text-xs text-muted-foreground">
                        <p>
                            By subscribing, you agree to our{" "}
                            <a
                                href="/terms"
                                className="text-primary hover:underline"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                billing terms and automatic renewal policy
                            </a>
                            .
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // For urgent variants, show full upgrade prompt
    const content = (
        <div className="space-y-4">
            <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">Plus Features</h3>
                <p className="text-muted-foreground">{getUpgradeMessage()}</p>
            </div>

            {error && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
                    <p className="text-sm text-destructive">{error}</p>
                </div>
            )}

            <div className="space-y-2">
                <h4 className="font-medium text-sm">Choose your plan:</h4>

                <div className="space-y-2">
                    <Button
                        onClick={() => handleUpgrade("monthly")}
                        disabled={isUpgrading || isLoading}
                        variant="outline"
                        className="w-full justify-between"
                    >
                        <span>Monthly - {STORYDOODLE_PLUS_MESSAGING.pricing.monthly}</span>
                        {isUpgrading && <Spinner className="h-4 w-4" />}
                    </Button>

                    <Button
                        onClick={() => handleUpgrade("annual")}
                        disabled={isUpgrading || isLoading}
                        variant="outline"
                        className="w-full justify-between"
                    >
                        <span>Annual - {STORYDOODLE_PLUS_MESSAGING.pricing.annual}</span>
                        <span className="text-success text-xs">Save 17%</span>
                    </Button>
                </div>

                <div className="text-xs text-muted-foreground mt-3 space-y-1">
                    <p>By subscribing, you agree to our billing terms and automatic renewal policy.</p>
                    <p>
                        View our{" "}
                        <a
                            href="/terms"
                            className="text-primary hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Terms of Service
                        </a>{" "}
                        and{" "}
                        <a
                            href="/privacy"
                            className="text-primary hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Privacy Policy
                        </a>
                        .
                    </p>
                </div>
            </div>

            {onDismiss && (
                <div className="text-center">
                    <button onClick={handleDismiss} className="text-sm text-muted-foreground hover:text-foreground">
                        Maybe later
                    </button>
                </div>
            )}
        </div>
    );

    if (variant === "modal") {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <Card className="max-w-md w-full p-6">{content}</Card>
            </div>
        );
    }

    return <Card className="p-6">{content}</Card>;
}
